// Phase 7 D3 — Track C T-C1. SettingsNav primitive.
//
// Responsive shell-internal navigation:
//   * desktop (≥768px) — vertical sidebar, 220px column. Each link is a
//     `<NavLink>` rendered as an `<a>` so keyboard tab order matches DOM
//     order and `aria-current="page"` is automatic.
//   * mobile (<768px) — horizontal pill-tab strip with overflow scroll.
//     Same NavLink set, restyled via Tailwind responsive utilities (the
//     responsive switch lives in CSS — no JS / matchMedia gating).
//
// Q-1: "sidebar with 5 NavLinks" / "mobile pill-tabs with overflow scroll".
//
// Tab order stays linear: each link is a focusable element in document
// order, no `tabIndex` overrides.

import { NavLink } from 'react-router-dom';
import { copy } from '@dailydeck/shared/copy';
import { cn } from '../../lib/cn.js';

interface SettingsNavItem {
  to: string;
  label: string;
}

const NAV_ITEMS: readonly SettingsNavItem[] = [
  { to: '/settings/account', label: copy.settings.shell.nav.account },
  { to: '/settings/theme', label: copy.settings.shell.nav.theme },
  { to: '/settings/devices', label: copy.settings.shell.nav.devices },
  { to: '/settings/integrations', label: copy.settings.shell.nav.integrations },
  { to: '/settings/notifications', label: copy.settings.shell.nav.notifications },
];

export function SettingsNav(): JSX.Element {
  return (
    <nav
      aria-label={copy.settings.shell.navAriaLabel}
      // Mobile (default): horizontal pill strip, scrollable.
      // Desktop (md+): vertical 220px sidebar.
      className={cn(
        'flex flex-row gap-1 overflow-x-auto pb-2',
        'md:w-[220px] md:flex-col md:gap-1 md:pb-0',
      )}
    >
      <ul role="list" className="flex flex-row gap-1 md:flex-col md:gap-1">
        {NAV_ITEMS.map((item) => (
          <li key={item.to} className="shrink-0">
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'block whitespace-nowrap rounded-md px-3 py-2 text-body-strong transition-colors duration-fast',
                  'hover:bg-surface-raised',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-default focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
                  // Mobile (pill): rounded-full + visible border for affordance.
                  'border border-border-subtle md:border-0',
                  isActive
                    ? 'bg-accent-subtle text-accent-pressed'
                    : 'text-text-primary',
                )
              }
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
